<template>
  <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
    <header class="mb-8 text-center sm:text-left">
      <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
        Minhas
        <span class="bg-clip-text text-transparent bg-gradient-to-r from-[#7B2CBF] to-[#A367E1]">Receitas</span>
      </h1>
      <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
        Acompanhe, gerencie e analise suas fontes de renda.
      </p>
    </header>

    <div class="flex justify-center mb-8">
      <nav class="segmented-control" role="tablist">
        <button @click="activeTab = 'list'" :class="{ 'active': activeTab === 'list' }" role="tab">
        <span class="material-symbols-outlined">list_alt</span>
         Receitas
        </button>
        <button @click="activeTab = 'analytics'" :class="{ 'active': activeTab === 'analytics' }" role="tab">
          <span class="material-symbols-outlined">monitoring</span>
          Análise de Receitas
        </button>
      </nav>
    </div>

    <transition name="fade" mode="out-in">
      <div v-if="activeTab === 'list'" key="list">
        <div class="panel mb-6">
          <div class="flex flex-wrap items-center gap-4">
             <button @click="handleNovaReceita" class="btn btn-primary">
              <span class="material-symbols-outlined mr-2">add_circle</span>
              Adicionar Receita
            </button>
            <button @click="exportToCSV" class="btn btn-secondary">
              <span class="material-symbols-outlined mr-2">download</span>
              Exportar Tabela
            </button>
          </div>
          <hr class="my-4 border-slate-200/80 dark:border-slate-700/60" />
          <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
            <select v-model="statusFiltro" class="form-select">
              <option value="">Status</option>
              <option value="PENDENTE">Pendente</option>
              <option value="RECEBIDO">Recebido</option>
            </select>
            <select v-model="anoSelecionado" class="form-select">
              <option value="">Ano</option>
              <option v-for="ano in anosDisponiveis" :key="ano" :value="ano">{{ ano }}</option>
            </select>
            <select v-model="mesSelecionado" class="form-select col-span-2 md:col-span-1">
              <option value="">Mês</option>
              <option v-for="mes in meses" :key="mes.valor" :value="mes.valor">{{ mes.nome }}</option>
            </select>
          </div>
        </div>

        <div class="panel hidden sm:block">
          <div class="overflow-x-auto">
            <table class="w-full min-w-[640px] text-sm text-left">
              <thead class="text-xs text-slate-500 dark:text-slate-400 uppercase">
                <tr>
                  <th class="px-6 py-3 font-semibold">Descrição</th>
                  <th class="px-6 py-3 font-semibold">Valor</th>
                  <th class="px-6 py-3 font-semibold">Data Receb.</th>
                  <th class="px-6 py-3 font-semibold">Status</th>
                  <th class="px-6 py-3 font-semibold text-center">Ações</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-200/80 dark:divide-slate-700/60">
                <tr v-if="sortedReceitas.length === 0">
                  <td colspan="5" class="empty-state h-48">
                    Nenhuma receita encontrada para os filtros selecionados.
                  </td>
                </tr>
                <tr v-for="receitaItem in sortedReceitas" :key="receitaItem.id"
                  class="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                  <td class="px-6 py-4 font-medium text-slate-900 dark:text-white whitespace-nowrap truncate max-w-xs">
                    <div class="flex items-center gap-3">
                      <div class="w-2 h-8 rounded-full bg-emerald-500"></div>
                      <span class="truncate">{{ receitaItem.descricao }}</span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-emerald-600 dark:text-emerald-400 font-semibold">{{
                    formatCurrency(receitaItem.valor) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-slate-500 dark:text-slate-400">{{
                    formatDate(receitaItem.dataRecebimento) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <select v-model="receitaItem.status" @change="updateStatus(receitaItem)" class="form-select-sm"
                      :class="getStatusClass(receitaItem).text">
                      <option value="PENDENTE">Pendente</option>
                      <option value="RECEBIDO">Recebido</option>
                    </select>
                  </td>
                  <td class="px-6 py-4 text-center whitespace-nowrap">
                    <div class="flex items-center justify-center gap-1">
                      <button @click="handleEditReceita(receitaItem)" title="Editar Receita"
                        class="btn-action-icon text-sky-600 dark:text-sky-400">
                        <span class="material-symbols-outlined text-lg">edit</span>
                      </button>
                      <button @click="confirmDelete(receitaItem.id)" title="Excluir Receita"
                        class="btn-action-icon text-rose-500 dark:text-rose-400">
                        <span class="material-symbols-outlined text-lg">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot class="bg-slate-50 dark:bg-slate-900/50 font-semibold">
                <tr>
                  <td colspan="4" class="px-6 py-3 text-right text-slate-700 dark:text-slate-200">Total a Receber
                    (Pendente):</td>
                  <td class="px-6 py-3 text-left text-slate-900 dark:text-white">{{
                    formatCurrency(totalReceitasPendentes) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div class="block sm:hidden space-y-4">
          <div v-if="sortedReceitas.length === 0" class="empty-state mt-6">
            Nenhuma receita encontrada.
          </div>
          <div v-for="receitaItem in sortedReceitas" :key="`card-${receitaItem.id}`"
            class="mobile-card border-l-emerald-500">
            <div class="flex justify-between items-start">
              <p class="font-bold text-slate-800 dark:text-slate-100 flex-1 pr-4">{{ receitaItem.descricao }}</p>
              <p class="font-bold text-lg text-emerald-600 dark:text-emerald-400">{{ formatCurrency(receitaItem.valor)
                }}</p>
            </div>
            <div
              class="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-base text-slate-500 dark:text-slate-400">event</span>
                <span class="text-slate-700 dark:text-slate-300">{{ formatDate(receitaItem.dataRecebimento) }}</span>
              </div>
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-base" :class="getStatusClass(receitaItem).text">
                  {{ getStatusClass(receitaItem).icon }}
                </span>
                <select v-model="receitaItem.status" @change="updateStatus(receitaItem)"
                  :class="['form-select-sm flex-1', getStatusClass(receitaItem).text]">
                  <option value="PENDENTE">Pendente</option>
                  <option value="RECEBIDO">Recebido</option>
                </select>
              </div>
            </div>
            <div class="mt-3 flex justify-end">
              <div class="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700/50 p-1">
                <button @click="handleEditReceita(receitaItem)" title="Editar Receita"
                  class="btn-action-icon text-sky-600 dark:text-sky-400">
                  <span class="material-symbols-outlined text-lg">edit</span>
                </button>
                <button @click="confirmDelete(receitaItem.id)" title="Excluir Receita"
                  class="btn-action-icon text-rose-500 dark:text-rose-400">
                  <span class="material-symbols-outlined text-lg">delete</span>
                </button>
              </div>
            </div>
          </div>
          <div v-if="sortedReceitas.length > 0"
            class="panel mt-4 text-right font-semibold text-slate-800 dark:text-slate-100">
            Total a Receber: {{ formatCurrency(totalReceitasPendentes) }}
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade" mode="out-in">
      <div v-if="activeTab === 'analytics'" key="analytics">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div class="panel text-center">
            <h3 class="panel-title">Total Recebido (Filtro)</h3>
            <p class="text-4xl font-bold text-emerald-600 dark:text-emerald-400 my-4">{{
              formatCurrency(totalRecebidoFiltrado) }}</p>
          </div>
          <div class="panel text-center">
            <h3 class="panel-title">Total a Receber (Pendente)</h3>
            <p class="text-4xl font-bold text-amber-500 dark:text-amber-400 my-4">{{
              formatCurrency(totalReceitasPendentes) }}</p>
          </div>
          <div class="lg:col-span-2 panel">
            <h3 class="panel-title">Receitas por Status (Filtro)</h3>
            <div class="h-80">
              <Apexchart v-if="dadosGraficoStatus.series.some(s => s > 0)" type="donut" height="100%"
                :options="opcoesGraficoStatus" :series="dadosGraficoStatus.series"></Apexchart>
              <div v-else class="empty-state h-full">
                <span class="material-symbols-outlined text-5xl">pie_chart</span>
                <span>Sem dados para exibir.</span>
              </div>
            </div>
          </div>
          <div class="lg:col-span-2 panel">
            <h3 class="panel-title">Histórico de Receitas (Últimos 12 Meses)</h3>
            <div class="h-80">
              <Bar
                v-if="dadosGraficoHistorico.datasets[0].data.some(d => d > 0) || dadosGraficoHistorico.datasets[1].data.some(d => d > 0)"
                :data="dadosGraficoHistorico" :options="opcoesGraficoHistorico" />
              <div v-else class="empty-state h-full">
                <span class="material-symbols-outlined text-5xl">monitoring</span>
                <span>Dados insuficientes para o histórico.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showModal" @click.self="closeModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div class="modal-panel max-w-md">
          <header class="modal-header">
            <h3 class="modal-title">
              {{ isEditing ? 'Editar Receita' : 'Adicionar Nova Receita' }}
            </h3>
            <button @click="closeModal" title="Fechar modal" :disabled="isSubmitting" class="btn-action-icon">
              <span class="material-symbols-outlined">close</span>
            </button>
          </header>
          <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
            <div>
              <label class="form-label">Descrição</label>
              <input v-model="receita.descricao" type="text" required placeholder="Ex: Salário Mensal"
                class="form-input" />
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="form-label">Valor (R$)</label>
                <input v-model.number="receita.valor" type="number" step="0.01" required placeholder="0.00"
                  class="form-input" />
              </div>
              <div>
                <label class="form-label">Data de Recebimento</label>
                <input v-model="receita.dataRecebimento" type="date" required class="form-input" />
              </div>
            </div>
            <div>
              <label class="form-label">Status</label>
              <select v-model="receita.status" required class="form-select">
                <option value="PENDENTE">Pendente</option>
                <option value="RECEBIDO">Recebido</option>
              </select>
            </div>
            <footer class="pt-4 flex justify-end gap-3">
              <button @click="closeModal" type="button" :disabled="isSubmitting"
                class="btn btn-secondary">Cancelar</button>
              <button type="submit" :disabled="isSubmitting" class="btn btn-primary">
                <span v-if="isSubmitting" class="material-symbols-outlined animate-spin mr-2">progress_activity</span>
                {{ isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Adicionar') }}
              </button>
            </footer>
          </form>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showDeleteConfirm" @click.self="showDeleteConfirm = false"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div class="modal-panel max-w-sm">
          <div class="p-8 text-center">
            <span class="material-symbols-outlined text-5xl text-rose-500 dark:text-rose-400 mx-auto">warning</span>
            <h3 class="mt-4 text-xl font-semibold">Excluir Receita</h3>
            <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">Tem certeza que deseja excluir esta receita?</p>
            <div class="mt-6 flex justify-center gap-3">
              <button @click="showDeleteConfirm = false" type="button" class="btn btn-secondary">Cancelar</button>
              <button @click="executeDelete" class="btn bg-rose-600 hover:bg-rose-700 text-white">Sim, Excluir</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showUnsavedChangesConfirm" @click.self="showUnsavedChangesConfirm = false"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div class="modal-panel max-w-md">
          <div class="p-8 text-center">
            <span class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">warning</span>
            <h3 class="mt-4 text-xl font-semibold">Descartar Alterações?</h3>
            <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">Você possui alterações não salvas. Tem certeza
              que deseja sair?</p>
            <div class="mt-6 flex justify-center gap-3">
              <button @click="showUnsavedChangesConfirm = false" type="button"
                class="btn btn-secondary">Cancelar</button>
              <button @click="handleConfirmClose" class="btn bg-rose-600 hover:bg-rose-700 text-white">Sim,
                Descartar</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, defineAsyncComponent, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';

const Bar = defineAsyncComponent(() => import('vue-chartjs').then(module => module.Bar));
const Apexchart = defineAsyncComponent(() => import('vue3-apexcharts'));

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const router = useRouter();
const allReceitas = ref([]);
const showModal = ref(false);
const isSubmitting = ref(false);
const isEditing = ref(false);
const showDeleteConfirm = ref(false);
const itemParaDeletar = ref(null);
const activeTab = ref('list');

const isMobile = ref(false);
const checkDevice = () => {
  isMobile.value = window.innerWidth < 640;
};

const getInitialReceita = () => ({
  id: null, descricao: '', valor: null,
  dataRecebimento: '', status: 'PENDENTE',
});

const receita = ref(getInitialReceita());

const apiUrl = 'https://backend.controlease.com.br/receitas';

const mesSelecionado = ref('');
const anoSelecionado = ref('');
const statusFiltro = ref('');

const pristineReceita = ref(null);
const showUnsavedChangesConfirm = ref(false);

const isFormDirty = computed(() => {
  if (!pristineReceita.value) return false;
  return JSON.stringify(receita.value) !== JSON.stringify(pristineReceita.value);
});

const isDarkMode = computed(() => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);

const meses = [
  { nome: 'Jan', valor: '01' }, { nome: 'Fev', valor: '02' }, { nome: 'Mar', valor: '03' },
  { nome: 'Abr', valor: '04' }, { nome: 'Mai', valor: '05' }, { nome: 'Jun', valor: '06' },
  { nome: 'Jul', valor: '07' }, { nome: 'Ago', valor: '08' }, { nome: 'Set', valor: '09' },
  { nome: 'Out', valor: '10' }, { nome: 'Nov', valor: '11' }, { nome: 'Dez', valor: '12' }
];

const getMesAtual = () => String(new Date().getMonth() + 1).padStart(2, '0');
const getAnoAtual = () => String(new Date().getFullYear());

const authConfig = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const fetchReceitas = async () => {
  try {
    const response = await axios.get(apiUrl, authConfig());
    allReceitas.value = response.data;
  } catch (error) { console.error('Erro ao buscar receitas:', error); }
};

const exportToCSV = () => {
  if (sortedReceitas.value.length === 0) {
    alert("Não há dados para exportar com os filtros atuais.");
    return;
  }
  const headers = ["Descrição", "Valor", "Data Recebimento", "Status"];
  const formatCSVField = (field) => `"${String(field ?? '').replace(/"/g, '""')}"`;
  const rows = sortedReceitas.value.map(item => [
    item.descricao,
    formatCurrency(item.valor, false).replace('.', '').replace(',', '.'),
    formatDate(item.dataRecebimento),
    item.status
  ].map(formatCSVField).join(','));
  const csv = "\uFEFF" + headers.join(',') + '\n' + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `export_receitas_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const filteredReceitas = computed(() => {
  return allReceitas.value.filter(r => {
    if (!r.dataRecebimento) return false;
    const [year, month] = r.dataRecebimento.split('-');
    const mesOk = !mesSelecionado.value || month === mesSelecionado.value;
    const anoOk = !anoSelecionado.value || year === anoSelecionado.value;
    const statusOk = !statusFiltro.value || r.status === statusFiltro.value;
    return mesOk && anoOk && statusOk;
  });
});

const sortedReceitas = computed(() => {
  return [...filteredReceitas.value].sort((a, b) => new Date(a.dataRecebimento) - new Date(b.dataRecebimento));
});

const anosDisponiveis = computed(() => {
  const anos = new Set(allReceitas.value.map(r => r.dataRecebimento?.split('-')[0]).filter(Boolean));
  return Array.from(anos).sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
});

const totalRecebidoFiltrado = computed(() =>
  (filteredReceitas.value || [])
    .filter(r => r.status === 'RECEBIDO')
    .reduce((total, r) => total + (parseFloat(r.valor) || 0), 0)
);

const totalReceitasPendentes = computed(() =>
  (filteredReceitas.value || [])
    .filter(r => r.status === 'PENDENTE')
    .reduce((total, r) => total + (parseFloat(r.valor) || 0), 0)
);

const dadosGraficoStatus = computed(() => ({
  series: [totalRecebidoFiltrado.value, totalReceitasPendentes.value],
  labels: ['Recebido', 'Pendente']
}));

const opcoesGraficoStatus = computed(() => ({
  chart: { type: 'donut', background: 'transparent' },
  labels: dadosGraficoStatus.value.labels,
  colors: ['#10B981', '#F59E0B'],
  dataLabels: { enabled: false },
  legend: {
    position: 'bottom',
    labels: { colors: isDarkMode.value ? '#CBD5E1' : '#475569' },
    formatter: (seriesName, opts) => {
      const valor = opts.w.globals.series[opts.seriesIndex];
      return `${seriesName}: ${formatCurrency(valor)}`;
    }
  },
  tooltip: { theme: isDarkMode.value ? 'dark' : 'light', y: { formatter: (val) => formatCurrency(val) } },
  stroke: { width: 0 },
  plotOptions: { pie: { donut: { labels: { show: true, total: { show: true, label: 'Total', color: isDarkMode.value ? '#CBD5E1' : '#475569', formatter: (w) => formatCurrency(w.globals.seriesTotals.reduce((a, b) => a + b, 0)) } } } } }
}));

const dadosGraficoHistorico = computed(() => {
  const labels = [];
  const dataRecebido = [];
  const dataPendente = [];
  const hoje = new Date();
  for (let i = 11; i >= 0; i--) {
    const dataIteracao = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    labels.push(dataIteracao.toLocaleString('pt-BR', { month: 'short', year: '2-digit' }));
    const ano = dataIteracao.getFullYear();
    const mes = String(dataIteracao.getMonth() + 1).padStart(2, '0');
    const receitasDoMes = allReceitas.value.filter(r => r.dataRecebimento?.startsWith(`${ano}-${mes}`));
    dataRecebido.push(receitasDoMes.filter(r => r.status === 'RECEBIDO').reduce((s, r) => s + r.valor, 0));
    dataPendente.push(receitasDoMes.filter(r => r.status === 'PENDENTE').reduce((s, r) => s + r.valor, 0));
  }
  return {
    labels,
    datasets: [
      { label: 'Recebido', backgroundColor: '#10B981', data: dataRecebido, borderRadius: 4 },
      { label: 'Pendente', backgroundColor: '#F59E0B', data: dataPendente, borderRadius: 4 }
    ]
  };
});

const opcoesGraficoHistorico = computed(() => ({
  responsive: true, maintainAspectRatio: false,
  scales: {
    y: { stacked: true, grid: { color: isDarkMode.value ? 'rgba(148, 163, 184, 0.1)' : 'rgba(226, 232, 240, 1)' }, ticks: { color: isDarkMode.value ? '#94a3b8' : '#64748b' } },
    x: { stacked: true, grid: { display: false }, ticks: { color: isDarkMode.value ? '#94a3b8' : '#64748b' } }
  },
  plugins: {
    legend: { position: 'top', align: 'end', labels: { color: isDarkMode.value ? '#94a3b8' : '#64748b', boxWidth: 12, padding: 20 } },
    tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}` } }
  }
}));

const handleNovaReceita = () => {
  if (isMobile.value) {
    router.push({ name: 'ReceitasAdd' });
  } else {
    showAddReceitaModal();
  }
};

const handleEditReceita = (item) => {
  if (isMobile.value) {
    router.push({ name: 'ReceitasEdit', params: { id: item.id } });
  } else {
    editReceita(item);
  }
};

const showAddReceitaModal = () => {
  resetForm();
  pristineReceita.value = JSON.parse(JSON.stringify(receita.value));
  showModal.value = true;
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  try {
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${receita.value.id}`, receita.value, authConfig());
    } else {
      await axios.post(apiUrl, receita.value, authConfig());
    }
    await fetchReceitas();
    forceCloseModal();
  } catch (error) {
    console.error('Erro ao salvar:', error); alert('Erro ao salvar.');
  } finally {
    isSubmitting.value = false;
  }
};

const editReceita = (receitaToEdit) => {
  receita.value = { ...receitaToEdit };
  pristineReceita.value = { ...receitaToEdit };
  isEditing.value = true;
  showModal.value = true;
};

const confirmDelete = (id) => {
  itemParaDeletar.value = id;
  showDeleteConfirm.value = true;
}

const executeDelete = async () => {
  if (itemParaDeletar.value === null) return;
  try {
    await axios.delete(`${apiUrl}/${itemParaDeletar.value}`, authConfig());
    await fetchReceitas();
  } catch (error) {
    console.error('Erro ao excluir:', error); alert('Erro ao excluir.');
  } finally {
    showDeleteConfirm.value = false;
    itemParaDeletar.value = null;
  }
};

const closeModal = () => {
  if (isSubmitting.value) return;
  if (isFormDirty.value) {
    showUnsavedChangesConfirm.value = true;
  } else {
    forceCloseModal();
  }
};

const forceCloseModal = () => { showModal.value = false; resetForm(); };
const handleConfirmClose = () => { showUnsavedChangesConfirm.value = false; forceCloseModal(); };

const updateStatus = async (receitaToUpdate) => {
  try {
    await axios.put(`${apiUrl}/${receitaToUpdate.id}`, receitaToUpdate, authConfig());
  } catch (error) {
    console.error('Erro ao atualizar status:', error);
    alert('Erro ao atualizar status.');
    await fetchReceitas();
  }
};

const resetForm = () => {
  receita.value = getInitialReceita();
  isEditing.value = false;
  pristineReceita.value = null;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

const formatCurrency = (value, withSymbol = true) => (typeof value === 'number' ? value : 0).toLocaleString('pt-BR', withSymbol ? { style: 'currency', currency: 'BRL' } : { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getStatusClass = (receitaItem) => {
  if (receitaItem.status === 'PENDENTE') {
    return { text: 'text-amber-600 dark:text-amber-400 font-semibold', icon: 'pending' };
  }
  if (receitaItem.status === 'RECEBIDO') {
    return { text: 'text-green-600 dark:text-green-400 font-semibold', icon: 'check_circle' };
  }
  return { text: 'text-slate-600 dark:text-slate-300', icon: 'help' };
};

onMounted(() => {
  mesSelecionado.value = getMesAtual();
  anoSelecionado.value = getAnoAtual();
  fetchReceitas();
  checkDevice();
  window.addEventListener('resize', checkDevice);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkDevice);
});
</script>


<style>
.form-input,
.form-select {
  @apply w-full px-3 py-2 text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700/50 border border-solid border-slate-300 dark:border-slate-600 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500/70 focus:border-purple-500;
}
.form-select {
  @apply appearance-none py-2.5;
}
.form-select-sm {
  @apply text-xs appearance-none block w-full px-2 py-1 font-normal bg-transparent border-0 focus:ring-0 focus:outline-none rounded-md cursor-pointer;
}
.btn-action-icon {
  @apply h-8 w-8 grid place-items-center rounded-lg transition-colors text-slate-500 dark:text-slate-400 hover:bg-slate-200/70 dark:hover:bg-slate-700/50;
}
.btn {
  @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60;
}
.btn-primary {
  @apply btn bg-[#5A189A] hover:bg-[#3C096C] text-white shadow-lg shadow-[#5A189A]/20;
}
.btn-secondary {
  @apply btn bg-white/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm;
}
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(-20px);
}
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.empty-state { @apply flex flex-col items-center justify-center text-center text-slate-500 dark:text-slate-400 p-4 min-h-[100px]; }
.panel {
  @apply bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20 p-4 sm:p-5;
}
.modal-panel {
  @apply bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full ring-1 ring-black/10 dark:ring-white/10;
}
.modal-header {
  @apply px-6 py-4 border-b border-slate-200/80 dark:border-slate-700/60 flex justify-between items-center;
}
.modal-title {
  @apply text-xl font-semibold text-slate-800 dark:text-slate-100;
}
.mobile-card {
  @apply bg-white dark:bg-slate-800/80 shadow-lg rounded-lg p-4 border-l-4;
}
.segmented-control {
  @apply inline-flex rounded-2xl p-1 bg-slate-200/70 dark:bg-slate-800/80;
}
.segmented-control button {
  @apply inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 text-sm font-semibold rounded-xl transition-all;
}
.segmented-control button.active {
  @apply bg-white dark:bg-slate-700 text-[#3C096C] dark:text-[#D9BFFB] shadow-md;
}
.segmented-control button:not(.active) {
  @apply text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white;
}
.panel-title {
  @apply text-lg font-semibold text-slate-800 dark:text-slate-100 mb-4;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity .3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>